import { createPortal } from 'react-dom';
import styled from 'styled-components';
import tinycolor from 'tinycolor2';
import { lowerOpacity } from './constants';
import { areOccurrencesEqual, Occurrence, Pattern } from './models/Pattern';
import PatternTooltip from './PatternTooltip';

export const patternHighlightClass = 'pattern-highlight';
const EllipsisLine = styled.line<{ $color: string; $colorOpacity: number }>`
  stroke: ${(styles) => styles.$color};
  stroke-dasharray: 5;
  stroke-opacity: ${(styles) => styles.$colorOpacity};
  stroke-width: 4;
`;

const PatternHighlight = ({
  color,
  colorOpacity,
  coordinates,
  hasEllipsisLeft,
  hasEllipsisRight,
  hoveredOccurrenceIndex,
  hoveredOccurrences,
  hoveredPatternIDs,
  id,
  occurrenceIndex,
  page,
  pattern,
  shouldShowTooltip
}: {
  color: string;
  colorOpacity: number;
  coordinates: [number, number][];
  hasEllipsisLeft: boolean;
  hasEllipsisRight: boolean;
  hoveredOccurrenceIndex: number | undefined;
  hoveredOccurrences: Occurrence[];
  hoveredPatternIDs: string[];
  id: string;
  occurrenceIndex: number;
  page: SVGElement;
  pattern: Pattern;
  shouldShowTooltip: boolean;
}) => {
  // The highlights are recomputed after the patterns changed: for one render a segment may still belong to an entry
  // that was just hidden (Suggestions panel of a discovered family).
  const occurrence = pattern?.occurrences[occurrenceIndex];
  if (pattern === undefined || occurrence === undefined) return null;
  if (coordinates.length === 1) coordinates.push(coordinates[0]);

  const firstCoordinate = coordinates[0];
  const isHovered = hoveredPatternIDs.includes(id) && hoveredOccurrenceIndex === occurrenceIndex;
  const lastCoordinate = coordinates[coordinates.length - 1];
  const shouldBeTransparent =
    (hoveredPatternIDs.length > 0 && !hoveredPatternIDs.includes(id)) ||
    (hoveredOccurrenceIndex !== undefined && occurrenceIndex !== hoveredOccurrenceIndex) ||
    (hoveredOccurrences.length > 0 &&
      !hoveredOccurrences.some((hoveredOccurrence) =>
        areOccurrencesEqual(occurrence, hoveredOccurrence)
      ));

  if (shouldBeTransparent) colorOpacity = lowerOpacity;

  const chipWidth = (pattern.label?.length ?? 0) * 6 + 8;

  return createPortal(
    <PatternTooltip pattern={pattern} isOpen={shouldShowTooltip && isHovered && !hasEllipsisLeft}>
      <g className={patternHighlightClass} tabIndex={-1}>
        {hasEllipsisLeft ? (
          <g>
            <EllipsisLine
              $color={color}
              $colorOpacity={colorOpacity}
              x1={firstCoordinate[0]}
              x2={firstCoordinate[0] - 25}
              y1={firstCoordinate[1] - 8}
              y2={firstCoordinate[1] - 8}
            />
            <EllipsisLine
              $color={color}
              $colorOpacity={colorOpacity}
              x1={firstCoordinate[0]}
              x2={firstCoordinate[0] - 25}
              y1={firstCoordinate[1] + 8}
              y2={firstCoordinate[1] + 8}
            />
          </g>
        ) : null}
        <polyline
          points={coordinates.map(([x, y]) => `${x},${y}`).join(' ')}
          style={{
            cursor: 'pointer',
            fill: 'none',
            stroke: color,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeOpacity: colorOpacity,
            strokeWidth: 20
          }}
        />
        {!hasEllipsisLeft && pattern.label && (
          // The label names the pattern beside its colour (audit F07: colour alone carried identity in the sheet).
          <g opacity={shouldBeTransparent ? 0.35 : 1} pointerEvents="none">
            <rect
              fill={color}
              height={13}
              rx={3}
              stroke="#ffffff"
              strokeWidth={1}
              width={chipWidth}
              x={firstCoordinate[0] - chipWidth / 2}
              y={firstCoordinate[1] - 25}
            />
            <text
              dominantBaseline="central"
              fill={tinycolor.mostReadable(color, ['#ffffff', '#0b0b0b']).toHexString()}
              fontFamily="sans-serif"
              fontSize={9}
              fontWeight={700}
              textAnchor="middle"
              x={firstCoordinate[0]}
              y={firstCoordinate[1] - 18.5}
            >
              {pattern.label}
            </text>
          </g>
        )}
        {hasEllipsisRight ? (
          <g>
            <EllipsisLine
              $color={color}
              $colorOpacity={colorOpacity}
              x1={lastCoordinate[0]}
              x2={lastCoordinate[0] + 25}
              y1={lastCoordinate[1] - 8}
              y2={lastCoordinate[1] - 8}
            />
            <EllipsisLine
              $color={color}
              $colorOpacity={colorOpacity}
              x1={lastCoordinate[0]}
              x2={lastCoordinate[0] + 25}
              y1={lastCoordinate[1] + 8}
              y2={lastCoordinate[1] + 8}
            />
          </g>
        ) : null}
      </g>
    </PatternTooltip>,
    page
  );
};

export default PatternHighlight;
